import { HTTP } from "@/shared/api"
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin"
import { jwtDecode } from "jwt-decode"

export interface IAuthTokenReceived {
    accessToken: string
    expirationDate: string
}

export interface IAuthToken {
    token: string
    expirationTimestamp: number
}

export interface IAuthTokenDecoded {
    id: string
    audiences: string
    "LOCAL AUTHORITY": string
    unique_name: string
    role: string
    nbf: number
    exp: number
    iat: number
    iss: string
    aud: string
}

export class AuthSecureStorage {
    private prefix: string

    constructor(prefix: string) {
        this.prefix = prefix
    }

    public async get(): Promise<IAuthToken> {
        try {
            const tokenPref = await SecureStoragePlugin.get({ key: `${this.prefix}-AuthToken` })
            const expirationTimestampPref = await SecureStoragePlugin.get({ key: `${this.prefix}-AuthExpirationTimestamp` })

            const token = (tokenPref.value) ? tokenPref.value : ""
            const expirationTimestamp = (expirationTimestampPref.value) ? parseInt(expirationTimestampPref.value, 10) : 0

            if (!this.check({ token, expirationTimestamp }))
                throw new Error("Token not found or expired")

            HTTP.defaults.headers.common.Authorization = `Bearer ${token}`
            return {
                token,
                expirationTimestamp
            }

        }
        catch (error) {
            return {
                token: "",
                expirationTimestamp: 0
            }
        }
    }

    public check(token: IAuthToken): boolean {
        try {
            const now = Math.trunc(Date.now() / 1000)
            if (token.token && now < token.expirationTimestamp)
                return true
            return false
        }
        catch (error) {
            return false
        }
    }

    public async save(tokenReceived: IAuthTokenReceived): Promise<IAuthToken | null> {
        try {
            const saveTokenResult = await SecureStoragePlugin.set({ key: `${this.prefix}-AuthToken`, value: tokenReceived.accessToken })
            const saveExpirationTimestampResult = await SecureStoragePlugin.set({
                key: `${this.prefix}-AuthExpirationTimestamp`,
                value: new Date(tokenReceived.expirationDate).getTime().toString()
            })

            if (!saveTokenResult || !saveExpirationTimestampResult)
                throw new Error("Error saving token")

            HTTP.defaults.headers.common.Authorization = `Bearer ${tokenReceived.accessToken}`
            return {
                token: tokenReceived.accessToken,
                expirationTimestamp: new Date(tokenReceived.expirationDate).getTime()
            }
        }
        catch (error) {
            return null
        }

    }

    public decode(token: IAuthToken): IAuthTokenDecoded | null {
        try {
            if (!token.token)
                throw new Error("Token not found")
            return jwtDecode(token.token)
        }
        catch (error) {
            return null
        }

    }

    public async clear(): Promise<boolean> {
        try {
            const clearTokenResult = await SecureStoragePlugin.remove({ key: `${this.prefix}-AuthToken` })
            const clearExpirationTimestampResult = await SecureStoragePlugin.remove({ key: `${this.prefix}-AuthExpirationTimestamp` })

            if (!clearTokenResult || !clearExpirationTimestampResult)
                throw new Error("Error clearing token")

            delete HTTP.defaults.headers.common.Authorization
            return true
        }
        catch (error) {
            return false
        }
    }
}
